/**
 * @module routes.Sessions.hooks.useCoachBooking
 */
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { DateTime } from "luxon";

import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import {
  CoachProfileModel,
  CoachProfileResponse,
} from "app/models/scheduling/CoachProfileModel";
import {
  FacilityModel,
  FacilityResponse,
} from "app/models/scheduling/FacilityModel";
import { LessonRateModel } from "app/models/scheduling/LessonRateModel";
import {
  resetBookSession,
  setBookSession,
} from "app/actions/scheduling/bookSession";

/**
 * Booking with one named coach, from anywhere the app knows a coach by their
 * CoachNow user id — a card on Home, their profile page.
 *
 * The screen it opens is the coach's own calendar with a booking option already
 * chosen (frame 547:73414), so the athlete who asked to book with a person never
 * has to pick that person out of a facility again.
 *
 * The id is the thing to be careful about. The app addresses a coach by CoachNow
 * user id; the scheduling service carries that as `externalId` and keeps its own
 * unrelated `id`, which is what indexes their rates. Matching the two up wrongly
 * is silent — the booking simply starts with no coach, and `/sessions/new` shows a
 * spinner until a facility it will never get arrives.
 */
export default function useCoachBooking() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [starting, setStarting] = useState(false);

  const startBooking = useCallback(
    async (coachUserId: string, participantAthleteId: string | null = null) => {
      setStarting(true);

      // Asked for on the click rather than on mount: most athletes never book
      // from a coach card, and the screens this hook sits on are not booking
      // screens.
      const [facilities, coaches] = await Promise.all([
        schedulingFetchGet("/facilities").catch(() => []),
        schedulingFetchGet("/coach_profiles").catch(() => []),
      ]);

      setStarting(false);

      const coach = ((coaches || []) as CoachProfileResponse[])
        .map((response) => new CoachProfileModel(response))
        .find(
          (profile) =>
            profile.externalId === coachUserId || profile.id === coachUserId,
        );

      const facility = coach
        ? ((facilities || []) as FacilityResponse[])
            .map((response) => new FacilityModel(response))
            .find((option) => option.id === coach.facilityId)
        : undefined;

      // This coach takes no bookings through the app. The facility booking page
      // is where the athlete can still find someone who does — and it is what
      // `/sessions/new` would otherwise spin on.
      if (!coach || !facility) {
        history.push("/sessions/session_availability");
        return;
      }

      const rates = [...(coach.lessonRates || [])].sort(
        (a: LessonRateModel, b: LessonRateModel) =>
          (a.orderNumber ?? Number.MAX_SAFE_INTEGER) -
          (b.orderNumber ?? Number.MAX_SAFE_INTEGER),
      );
      // The coach's own default, and their first option where they have named
      // none — the calendar opens on it, so it is never nothing.
      const pricingValue = rates.find((rate) => rate.defaultRate) || rates[0] || null;

      // Whatever a previous booking left behind is cleared first: the state is a
      // single slice, and a stale time or rate from another coach outlives the
      // fields set here.
      dispatch(resetBookSession());
      dispatch(
        setBookSession({
          coach,
          facility,
          date: DateTime.utc().startOf("day").toISODate(),
          pricingValue,
          time: null,
          participantAthleteId,
        }),
      );
      history.push("/sessions/new");
    },
    [dispatch, history],
  );

  return { startBooking, starting };
}
